/**
 * Forum moderation notification contract (shared by the lambdas and the app).
 *
 * Approvers (Provider Owners, L4 — see `canModerateForum`) are told when a
 * member Forum post is waiting for approval. The text is deliberately
 * content-free: push titles/bodies transit FCM/APNs and sit on lock screens, so
 * they carry the author's FIRST NAME and a neutral line only — never the post
 * title or body (see `shared/sessions/push-privacy.ts`). The post itself is one
 * authenticated tap away on the moderation screen.
 */
import { pushSafeFirstName } from '../sessions/push-privacy'

/** `data.type` / notifications-table `type` for an approval request. */
export const FORUM_REVIEW_TYPE = 'FORUM_REVIEW'
export const FORUM_APPROVED_TYPE = 'FORUM_APPROVED'
export const FORUM_REJECTED_TYPE = 'FORUM_REJECTED'

/** In-app moderation (approval) screen. Tapping a FORUM_REVIEW push/row opens it. */
export const FORUM_REVIEW_PATH = '/announcements/review'

/**
 * Approval screen deep link for one post. `?post=` lets the screen say
 * "Already handled" when another approver got there first.
 */
export function forumReviewPath(postId: string | null | undefined): string {
  const id = postId?.trim()
  return id ? `${FORUM_REVIEW_PATH}?post=${encodeURIComponent(id)}` : FORUM_REVIEW_PATH
}

/**
 * Stable id for an approval-request row: one per (post, approver). Retries of
 * the async notify invoke map to the same id, so they can never twin.
 */
export function forumReviewNotificationId(postId: string, approverId: string): string {
  return `forum-review-${postId}-${approverId}`
}

/** Where an author lands for their own post's decision when the row has no path. */
export const FORUM_HOME_PATH = '/forum'

const FORUM_PUSH_TYPES: ReadonlySet<string> = new Set([
  FORUM_REVIEW_TYPE,
  FORUM_APPROVED_TYPE,
  FORUM_REJECTED_TYPE,
])

/** True for the three Forum moderation push / notification types. */
export function isForumModerationType(type: string | null | undefined): boolean {
  return Boolean(type) && FORUM_PUSH_TYPES.has(type as string)
}

export type ForumReviewMessage = {
  title: string
  body: string
}

/**
 * Neutral approval-request text: first name + a fixed line. Never includes the
 * post title or body.
 */
export function buildForumReviewMessage(
  authorDisplayName: string | null | undefined,
): ForumReviewMessage {
  const first = pushSafeFirstName(authorDisplayName, 'A member')
  return {
    title: 'New forum post awaiting approval',
    body: `${first} submitted a post for review.`,
  }
}
